<template>
  <b-modal
    v-model="internalVisible"
    size="xl"
    modal-class="destination-fullscreen-modal"
    hide-footer
    no-close-on-backdrop
    @hidden="onHidden"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center justify-content-between w-100">
        <h5 class="mb-0">
          <feather-icon
            icon="MapPinIcon"
            size="16"
            class="mr-50"
          />
          Definir Destino
        </h5>
        <button
          class="btn btn-sm btn-outline-secondary"
          @click="close"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
      </div>
    </template>

    <div class="destination-modal">
      <div class="destination-modal__filters">
        <div class="form-group mb-0">
          <label class="small mb-25 d-block">Tipo de Estabelecimento</label>
          <v-select
            v-model="filters.type_establishment_ids"
            :options="establishmentTypes"
            :reduce="(t) => t.id"
            label="establishment_type_name"
            placeholder="Todos"
            class="vselect"
            multiple
          />
        </div>
        <div class="form-group mb-0">
          <label class="small mb-25 d-block">Tipo de Ocorrência</label>
          <v-select
            v-model="filters.dispatch_type_ids"
            :options="dispatchTypes"
            :reduce="(t) => t.id"
            label="name"
            placeholder="Todos"
            class="vselect"
            multiple
          />
        </div>
        <div class="form-group mb-0">
          <label class="small mb-25 d-block">Motivo</label>
          <v-select
            v-model="filters.dispatch_reason_ids"
            :options="dispatchReasons"
            :reduce="(r) => r.id"
            label="name"
            placeholder="Todos"
            class="vselect"
            multiple
          />
        </div>
        <div class="form-group mb-0">
          <label class="small mb-25 d-block">Linha de Cuidado</label>
          <v-select
            v-model="filters.care_line_ids"
            :options="careLines"
            :reduce="(c) => c.id"
            label="name"
            placeholder="Todas"
            class="vselect"
            multiple
          />
        </div>
        <div class="form-group mb-0 destination-modal__search">
          <label class="small mb-25 d-block">Buscar</label>
          <input
            v-model="search"
            type="text"
            class="form-control"
            placeholder="Nome do estabelecimento..."
          />
        </div>
      </div>

      <div class="form-group destination-modal__observation">
        <label class="small mb-25 d-block">
          Observação (opcional — aplicada ao destino selecionado abaixo)
        </label>
        <input
          v-model="observation"
          type="text"
          class="form-control"
          maxlength="2000"
          placeholder="Observação..."
        />
      </div>

      <div class="destination-modal__body">
        <div class="destination-modal__list">
          <div
            v-if="loading"
            class="text-center py-4"
          >
            <b-spinner variant="success" />
          </div>
          <div
            v-else-if="!list.length"
            class="text-center text-muted py-4"
          >
            Nenhum estabelecimento encontrado com os filtros atuais.
          </div>
          <template v-else>
            <div class="destination-modal__cards">
              <div
                v-for="e in list"
                :key="e.id"
                class="destination-card"
                :class="{ 'destination-card--hover': hoveredId === e.id }"
                @mouseenter="hoveredId = e.id"
                @mouseleave="hoveredId = null"
              >
                <div class="destination-card__main">
                  <div class="destination-card__name">
                    {{ e.establishment_name }}
                    <span
                      v-if="e.type_establishment"
                      class="badge badge-light-secondary ml-50"
                      >{{ e.type_establishment.establishment_type_name }}</span
                    >
                  </div>
                  <div class="destination-card__address">
                    {{ formatAddress(e) }}
                  </div>
                  <div
                    v-if="e.distance_km !== undefined"
                    class="destination-card__distance"
                  >
                    <feather-icon
                      icon="NavigationIcon"
                      size="11"
                      class="mr-25"
                    />
                    {{ e.distance_km }} km da ocorrência
                  </div>
                </div>
                <button
                  class="btn btn-sm btn-success"
                  :disabled="saving"
                  @click="chooseEstablishment(e)"
                >
                  <b-spinner
                    v-if="saving && choosingId === e.id"
                    small
                    class="mr-25"
                  />
                  <feather-icon
                    v-else
                    icon="CheckCircleIcon"
                    size="13"
                    class="mr-25"
                  />
                  Selecionar
                </button>
              </div>
            </div>

            <div
              v-if="pagination.last_page > 1"
              class="d-flex justify-content-center p-1"
            >
              <pagination
                v-model="pagination.current_page"
                :options="opcoesPaginacao"
                :records="pagination.total"
                :per-page="pagination.per_page"
                @paginate="loadList"
              />
            </div>
          </template>
        </div>

        <div class="destination-modal__map">
          <GmapMap
            ref="mapRef"
            :center="mapCenter"
            :zoom="10"
            map-type-id="roadmap"
            style="width: 100%; height: 100%"
          >
            <GmapMarker
              v-if="hasOrigin"
              :position="originPosition"
              :icon="originIcon"
              title="Ocorrência"
            />
            <GmapMarker
              v-for="pin in mapPins"
              :key="'est-' + pin.id"
              :position="{
                lat: parseFloat(pin.latitude),
                lng: parseFloat(pin.longitude),
              }"
              :icon="pinIcon(pin)"
              :label="pinLabel(pin)"
              :title="pin.establishment_name"
              @click="focusEstablishment(pin)"
            />
            <GmapInfoWindow
              v-if="activePin"
              :position="{
                lat: parseFloat(activePin.latitude),
                lng: parseFloat(activePin.longitude),
              }"
              :options="infoWindowOptions"
              :opened="true"
              @closeclick="activePin = null"
            >
              <div class="destination-map-card">
                <div class="destination-map-card__icon">
                  <feather-icon
                    icon="MapPinIcon"
                    size="18"
                  />
                </div>
                <div class="destination-map-card__body">
                  <div class="destination-map-card__title">
                    {{ activePin.establishment_name }}
                  </div>
                  <div
                    v-if="activePin.type_establishment"
                    class="destination-map-card__subtitle"
                  >
                    {{ activePin.type_establishment.establishment_type_name }}
                  </div>
                  <div class="destination-map-card__address">
                    {{ formatAddress(activePin) }}
                  </div>
                  <div
                    v-if="activePin.distance_km !== undefined"
                    class="destination-map-card__distance"
                  >
                    <feather-icon
                      icon="NavigationIcon"
                      size="11"
                      class="mr-25"
                    />
                    {{ activePin.distance_km }} km da ocorrência
                  </div>
                  <button
                    class="btn btn-sm btn-success destination-map-card__action"
                    :disabled="saving"
                    @click="chooseEstablishment(activePin)"
                  >
                    <b-spinner
                      v-if="saving && choosingId === activePin.id"
                      small
                      class="mr-25"
                    />
                    <feather-icon
                      v-else
                      icon="CheckCircleIcon"
                      size="13"
                      class="mr-25"
                    />
                    Selecionar
                  </button>
                </div>
              </div>
            </GmapInfoWindow>
          </GmapMap>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import vSelect from "vue-select";
import Pagination from "vue-pagination-2";
import opcoesPaginacao from "@/libs/paginationOptions";

export default {
  name: "DefineDestinationModal",
  components: { vSelect, Pagination },
  props: {
    value: { type: Boolean, default: false },
    occurrenceInvolvedId: { type: [Number, String], default: null },
    originLat: { type: [Number, String], default: null },
    originLng: { type: [Number, String], default: null },
    occurrenceDispatchTypeId: { type: [Number, String], default: null },
    occurrenceDispatchReasonId: { type: [Number, String], default: null },
  },
  data() {
    return {
      filters: {
        type_establishment_ids: [],
        dispatch_type_ids: [],
        dispatch_reason_ids: [],
        care_line_ids: [],
      },
      search: "",
      // Incrementado a cada loadList() — usado pra descartar respostas de
      // requisições antigas que chegam depois de uma mais recente (ex:
      // abrir o modal dispara várias buscas de uma vez, e sem isso a
      // resposta "sem filtro" podia sobrescrever a resposta já filtrada).
      listRequestId: 0,
      establishmentTypes: [],
      dispatchTypes: [],
      dispatchReasons: [],
      careLines: [],
      list: [],
      mapPins: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
      },
      loading: false,
      saving: false,
      choosingId: null,
      hoveredId: null,
      activePin: null,
      observation: "",
      mapCenter: { lat: -15.77972, lng: -47.92972 },
      searchDebounceTimer: null,
      // Marcador vermelho pro ponto de origem (ocorrência) — mesmo estilo do
      // pin de ocorrência em Occurrence/Index.vue.
      originIcon: {
        url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
        scaledSize: { width: 44, height: 44 },
      },
      candidateIcon: {
        url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
        scaledSize: { width: 36, height: 36 },
      },
    };
  },
  computed: {
    opcoesPaginacao() {
      return opcoesPaginacao;
    },
    internalVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    hasOrigin() {
      return (
        this.originLat !== null &&
        this.originLat !== undefined &&
        this.originLng !== null &&
        this.originLng !== undefined
      );
    },
    originPosition() {
      return {
        lat: parseFloat(this.originLat),
        lng: parseFloat(this.originLng),
      };
    },
    // Sem isso o InfoWindow flutua colado na coordenada do pin (não sabe da
    // altura do ícone + label acima dele) e cobre o marcador. Altura
    // negativa empurra a janela pra cima, liberando o pin + label.
    infoWindowOptions() {
      return { pixelOffset: { width: 0, height: -70 } };
    },
  },
  watch: {
    value(visible) {
      if (visible) this.onOpen();
    },
    filters: {
      deep: true,
      handler() {
        this.loadList(1);
      },
    },
    search() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => this.loadList(1), 400);
    },
  },
  methods: {
    onOpen() {
      this.resetFilters();
      this.mapCenter = this.hasOrigin ? this.originPosition : this.mapCenter;
      this.loadFilterOptions();
      this.loadList(1);
    },
    onHidden() {
      this.$emit("input", false);
    },
    resetFilters() {
      // Toda ocorrência já tem Tipo e Motivo (são obrigatórios) — parte
      // filtrado por eles, já que é o critério mais óbvio pra achar o
      // destino certo. Linha de Cuidado não tem de onde vir (a ocorrência
      // não carrega esse dado hoje), então fica sempre em aberto.
      this.filters = {
        type_establishment_ids: [],
        dispatch_type_ids: this.occurrenceDispatchTypeId
          ? [Number(this.occurrenceDispatchTypeId)]
          : [],
        dispatch_reason_ids: this.occurrenceDispatchReasonId
          ? [Number(this.occurrenceDispatchReasonId)]
          : [],
        care_line_ids: [],
      };
      this.search = "";
      this.observation = "";
      this.hoveredId = null;
      this.activePin = null;
    },
    formatAddress(establishment) {
      const parts = [];
      let street = establishment.address || "";
      if (establishment.number) street += `, ${establishment.number}`;
      if (street) parts.push(street);
      if (establishment.complement) parts.push(establishment.complement);
      if (establishment.neighborhood) parts.push(establishment.neighborhood);

      const city = establishment.city ? establishment.city.name : "";
      const state = establishment.state ? establishment.state.code : "";
      const cityState = [city, state].filter(Boolean).join("/");
      if (cityState) parts.push(cityState);

      return parts.join(" - ") || "—";
    },
    // Nó operacional atual: $store.state.selected_node (setado pelo
    // NodeSelector), nunca o current_node_id do backend — esse costuma
    // ficar null pra vários usuários e faz os for-node voltarem vazios.
    currentNodeId() {
      return this.$store.state.selected_node
        ? this.$store.state.selected_node.id
        : null;
    },
    loadFilterOptions() {
      axios
        .get(
          `${this.$store.state.api}establishment_types?paginated=false&status=1`
        )
        .then((r) => {
          this.establishmentTypes = r.data.data || [];
        });
      axios
        .get(`${this.$store.state.api}dispatch_types/for-node`, {
          params: { node_id: this.currentNodeId() },
        })
        .then((r) => {
          this.dispatchTypes = r.data.data || [];
        });
      axios
        .get(`${this.$store.state.api}care_lines/for-node`, {
          params: { node_id: this.currentNodeId(), status: 1 },
        })
        .then((r) => {
          this.careLines = r.data.data || [];
        });
      // Motivo não é mais filtrado em cascata pelo Tipo — com multi-seleção
      // nos dois filtros ao mesmo tempo, a cascata complicaria mais do que
      // ajudaria. Carrega todos os motivos do nó de uma vez só.
      axios
        .get(`${this.$store.state.api}dispatch_reasons/for-node`, {
          params: { node_id: this.currentNodeId() },
        })
        .then((r) => {
          this.dispatchReasons = r.data.data || [];
        });
    },
    loadList(page = 1) {
      this.loading = true;
      // Só a resposta da requisição mais recente pode atualizar a lista —
      // evita que uma busca antiga (ex: a sem-filtro disparada ao abrir o
      // modal) chegue depois e sobrescreva um resultado já filtrado.
      this.listRequestId += 1;
      const requestId = this.listRequestId;
      const params = {
        page,
        per_page: this.pagination.per_page,
        ...this.filters,
        search: this.search,
        node_id: this.currentNodeId(),
      };
      if (this.hasOrigin) {
        params.lat = this.originLat;
        params.lng = this.originLng;
      }
      return axios
        .get(`${this.$store.state.api}establishments/for-destination`, {
          params,
        })
        .then((r) => {
          if (requestId !== this.listRequestId) return;
          this.list = r.data.data || [];
          this.mapPins = r.data.map_pins || [];
          this.pagination = { ...this.pagination, ...r.data.meta };
        })
        .catch(() => {
          if (requestId !== this.listRequestId) return;
          this.$message(
            "Erro",
            "Não foi possível carregar os estabelecimentos.",
            "error"
          );
        })
        .finally(() => {
          if (requestId === this.listRequestId) this.loading = false;
        });
    },
    pinIcon(pin) {
      const base =
        pin.id === this.hoveredId
          ? {
              ...this.candidateIcon,
              url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
            }
          : this.candidateIcon;
      // labelOrigin desloca o texto do marcador pra cima do ícone (por
      // padrão o label do Google Maps fica centralizado em cima do pin,
      // ilegível com o ícone embaixo). -18 dá espaço pro balão maior +
      // "rabinho" (CSS) não invadir o ícone.
      return {
        ...base,
        labelOrigin: {
          x: base.scaledSize.width / 2,
          y: -18,
        },
      };
    },
    pinLabel(pin) {
      return {
        text: pin.establishment_name,
        color: "#1a3c73",
        fontSize: "12.5px",
        fontWeight: "700",
        className: "destination-map-pin-label",
      };
    },
    focusEstablishment(pin) {
      this.hoveredId = pin.id;
      this.activePin = pin;
      this.mapCenter = {
        lat: parseFloat(pin.latitude),
        lng: parseFloat(pin.longitude),
      };
    },
    async chooseEstablishment(establishment) {
      if (!this.occurrenceInvolvedId) return;
      this.saving = true;
      this.choosingId = establishment.id;
      try {
        const res = await axios.post(
          `${this.$store.state.api}occurrence_involved_destinations`,
          {
            occurrence_involved_id: this.occurrenceInvolvedId,
            establishment_id: establishment.id,
            observation: this.observation || null,
          }
        );
        const saved = res.data.data || res.data;
        this.$message(
          "Destino definido",
          "Destino salvo com sucesso.",
          "success"
        );
        this.$emit("saved", saved);
        this.internalVisible = false;
      } catch (e) {
        this.$message("Erro", "Erro ao definir destino.", "error");
      } finally {
        this.saving = false;
        this.choosingId = null;
      }
    },
  },
};
</script>

<style>
/* Modal grande, mas não 100vw/100vh — isso cobria até o menu lateral do
   app. Sobra uma margem pra dar pra ver que ainda dá pra fechar/voltar.
   Precisa ser global (sem `scoped`) pra pegar as classes que o
   bootstrap-vue injeta fora do componente. */
.destination-fullscreen-modal .modal-dialog {
  max-width: 95vw;
  width: 95vw;
  height: 90vh;
  margin: 5vh auto;
}
.destination-fullscreen-modal .modal-content {
  height: 90vh;
  border-radius: 8px;
}
.destination-fullscreen-modal .modal-body {
  height: calc(90vh - 62px);
  overflow: hidden;
  padding: 12px 16px;
  display: flex;
}

/* Label acima dos pins de estabelecimento no mapa — precisa ser global
   porque o Google Maps renderiza o label fora da árvore do componente Vue.
   O ::after simula um "rabinho" apontando pro pin, pra não parecer um
   texto solto flutuando no mapa. */
.destination-map-pin-label {
  position: relative;
  display: inline-block;
  background: #fff;
  border: 1.5px solid #1a73e8;
  padding: 4px 11px;
  border-radius: 7px;
  box-shadow: 0 2px 6px rgba(26, 115, 232, 0.3);
  white-space: nowrap;
  letter-spacing: 0.15px;
}
.destination-map-pin-label::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -8px;
  transform: translateX(-50%);
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid #1a73e8;
}
.destination-map-pin-label::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -5.5px;
  transform: translateX(-50%);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #fff;
}

.destination-map-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 230px;
  max-width: 280px;
  padding: 2px;
}
.destination-map-card__icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #eef0fd;
  color: #2f2c84;
  display: flex;
  align-items: center;
  justify-content: center;
}
.destination-map-card__title {
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
}
.destination-map-card__subtitle {
  font-size: 0.72rem;
  color: #2f2c84;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 1px;
}
.destination-map-card__address {
  font-size: 0.78rem;
  color: #6e6b7b;
  margin-top: 4px;
}
.destination-map-card__distance {
  font-size: 0.75rem;
  color: #28c76f;
  margin-top: 4px;
}
.destination-map-card__action {
  margin-top: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style scoped>
.destination-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.destination-modal__filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr)) minmax(220px, 1.5fr);
  gap: 12px;
  margin-bottom: 10px;
}

.destination-modal__observation {
  margin-bottom: 10px;
}

.destination-modal__body {
  flex: 1;
  display: flex;
  gap: 14px;
  min-height: 0;
}

.destination-modal__list {
  width: 38%;
  min-width: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.destination-modal__map {
  flex: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0ddf5;
}

.destination-modal__cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.destination-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e0ddf5;
  border-radius: 8px;
  transition: border-color 0.15s, background 0.15s;
}

.destination-card--hover {
  border-color: #28c76f;
  background: #f4fdf8;
}

.destination-card__name {
  font-weight: 600;
  font-size: 0.9rem;
}

.destination-card__address {
  font-size: 0.78rem;
  color: #6e6b7b;
}

.destination-card__distance {
  font-size: 0.75rem;
  color: #28c76f;
  margin-top: 2px;
}
</style>
